/**
 * Diagnostic d'un calcul — réservé aux administrateurs de la plateforme.
 *
 * Le lanceur chronomètre ses propres phases et en déduit des pistes par des
 * règles explicites. Cette vue ne fait que les présenter : elle n'interprète
 * rien, pour que ce qui est affiché reste vérifiable.
 *
 * Le serveur retire déjà le diagnostic de la charge utile d'un utilisateur
 * normal. Ce composant ne s'affiche donc que lorsque la donnée est là — le
 * privilège est appliqué en amont, pas ici.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

const TONS = {
  fort: 'bg-error-500/10 border-error-500/30 text-error-400',
  moyen: 'bg-warning-500/10 border-warning-500/30 text-warning-400',
  faible: 'bg-dark-800 border-dark-700 text-dark-300',
};

const Mesure = ({ libelle, valeur, unite }) => (
  <div className="bg-dark-900 rounded-lg p-3">
    <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{libelle}</p>
    <p className="text-dark-100 font-mono">
      {valeur}
      {unite && <span className="text-dark-500 text-sm ml-1">{unite}</span>}
    </p>
  </div>
);

const DiagnosticSimulation = ({ diagnostic }) => {
  const { t } = useTranslation();
  if (!diagnostic?.mesures) return null;

  const m = diagnostic.mesures;
  const pistes = diagnostic.pistes || [];

  const mesures = [
    [t('diagnostic.dureeTotale'), m.duree_totale_min, 'min'],
    [t('diagnostic.iterations'), m.iterations_conception, null],
    [t('diagnostic.iterationsOptimiseur'), m.iterations_optimiseur, null],
    [t('diagnostic.cadence'), m.duree_iteration_conception_min, 'min'],
    [t('diagnostic.partAdjoint'), m.part_adjoint_pourcent, '%'],
    [t('diagnostic.dureeAdjoint'), m.duree_moyenne_adjoint_min, 'min'],
    [t('diagnostic.iterationsGmres'), m.iterations_gmres_moyennes, null],
    [t('diagnostic.picMemoire'), m.memoire_pic_mib, 'Mio'],
    [t('diagnostic.memoireReservee'), m.memoire_reservee_mib, 'Mio'],
    [t('diagnostic.memoireMachine'), m.memoire_machine_mib, 'Mio'],
    [t('diagnostic.pourcentReservation'), m.memoire_pourcent_reservation, '%'],
    [t('diagnostic.pourcentMachine'), m.memoire_pourcent_machine, '%'],
    [t('diagnostic.pourcentCoeurs'), m.coeurs_pourcent, '%'],
    [t('diagnostic.rangs'), m.rangs_mpi, null],
    [t('diagnostic.coeursMachine'), m.coeurs_machine, null],
    [t('diagnostic.machine'), m.machine_type, null],
    [t('diagnostic.machineCycleVie'), m.machine_cycle_vie, null],
    [t('diagnostic.codeRetour'), m.code_retour, null],
  ].filter(([, v]) => v !== null && v !== undefined);

  // La cause reconnue, s'il y en a une. Elle passe AVANT les chiffres : un
  // calcul qui a échoué se lit par sa cause, pas par sa consommation.
  const panne = m.panne || null;
  const libellePanne = {
    tue_faute_de_memoire: t('diagnostic.panneMemoire'),
    patch_inconnu: t('diagnostic.pannePatch'),
    optimisation_immobile: t('diagnostic.panneImmobile'),
  }[panne?.type];

  const horodatage = diagnostic.horodatage
    ? new Date(diagnostic.horodatage).toLocaleString()
    : null;

  return (
    <div className="space-y-6">
      <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3">
        <p className="text-primary-400 text-sm">{t('diagnostic.reserveAdmin')}</p>
      </div>

      {horodatage && (
        <p className="text-dark-500 text-xs font-mono">
          {t('diagnostic.lanceLe')} {horodatage}
          {diagnostic.case ? ` · ${diagnostic.case}` : ''}
          {diagnostic.simulation_id ? ` · ${diagnostic.simulation_id}` : ''}
        </p>
      )}

      {libellePanne && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-error-400/70 mb-1">
            {t('diagnostic.panneReconnue')}
          </p>
          <p className="text-error-300 text-sm font-medium">{libellePanne}</p>
          {panne.demande && (
            <p className="text-dark-300 text-xs mt-2 font-mono">
              « {panne.demande} » → {(panne.valides || []).join(' · ') || '—'}
            </p>
          )}
        </div>
      )}

      <div>
        <h3 className="text-dark-100 font-semibold mb-3">{t('diagnostic.mesures')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {mesures.map(([libelle, valeur, unite]) => (
            <Mesure key={libelle} libelle={libelle} valeur={valeur} unite={unite} />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-dark-100 font-semibold mb-3">
          {t('diagnostic.pistes')} ({pistes.length})
        </h3>
        {pistes.length === 0 ? (
          <p className="text-dark-400 text-sm">{t('diagnostic.aucunePiste')}</p>
        ) : (
          <div className="space-y-3">
            {pistes.map((p, i) => (
              <div key={i} className={`border rounded-lg p-4 ${TONS[p.niveau] || TONS.faible}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wider font-medium">
                    {t(`diagnostic.domaine.${p.domaine}`, p.domaine)}
                  </span>
                </div>
                <p className="text-dark-200 text-sm">{p.constat}</p>
                <p className="text-dark-300 text-sm mt-2">
                  <span className="text-dark-500">{t('diagnostic.aFaire')}</span>
                  {p.piste}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticSimulation;
